/**
 * Helpers for Shopify Ajax cart discount codes.
 */

function normalizeDiscountCodeEntry(entry) {
  if (typeof entry === "string") {
    return { code: entry.trim(), applicable: true };
  }
  return {
    code: String(entry?.code || "").trim(),
    applicable: entry?.applicable !== false,
  };
}

export function extractCartDiscountCodes(cart) {
  if (!cart || typeof cart !== "object") return [];

  const raw = cart.discount_codes;
  if (!Array.isArray(raw)) return [];

  return raw
    .map((entry) => normalizeDiscountCodeEntry(entry).code)
    .filter(Boolean);
}

/** Only codes Shopify accepted on the Ajax cart (applicable !== false). */
export function extractApplicableCartDiscountCodes(cart) {
  if (!cart || typeof cart !== "object") return [];

  const raw = cart.discount_codes;
  if (!Array.isArray(raw)) return [];

  return raw
    .map((entry) => normalizeDiscountCodeEntry(entry))
    .filter((entry) => entry.code && entry.applicable)
    .map((entry) => entry.code);
}

export function getPrimaryCartDiscountCode(cart) {
  const codes = extractApplicableCartDiscountCodes(cart);
  return codes[0] || null;
}

export function getCartDiscountCodeEntry(cart, code) {
  const normalized = String(code || "").trim().toLowerCase();
  if (!normalized || !cart) return null;

  const raw = cart.discount_codes;
  if (!Array.isArray(raw)) return null;

  for (const entry of raw) {
    const normalizedEntry = normalizeDiscountCodeEntry(entry);
    if (normalizedEntry.code.toLowerCase() === normalized) {
      return normalizedEntry;
    }
  }

  return null;
}

/** True when Shopify reports the code as applicable on the Ajax cart. */
export function isCartDiscountCodeApplied(cart, code) {
  const entry = getCartDiscountCodeEntry(cart, code);
  if (!entry) return false;
  return entry.applicable === true;
}

export function hasAppliedCartDiscountCode(cart) {
  return extractApplicableCartDiscountCodes(cart).length > 0;
}

function discountTotalsIncreased(beforeCart, afterCart) {
  const beforeApps = beforeCart?.cart_level_discount_applications || [];
  const afterApps = afterCart?.cart_level_discount_applications || [];
  if (afterApps.length > beforeApps.length) return true;

  const beforeDiscount = Number(beforeCart?.total_discount || 0);
  const afterDiscount = Number(afterCart?.total_discount || 0);
  if (afterDiscount > beforeDiscount) return true;

  return false;
}

/**
 * Interpret Shopify cart/update.js result for a submitted discount code.
 * HTTP 200 does not mean the code was accepted — check discount_codes[].applicable.
 */
export function evaluateCartDiscountApplyResult(beforeCart, afterCart, code) {
  const normalizedCode = String(code || "").trim().toLowerCase();
  if (!normalizedCode) {
    return {
      success: false,
      message: "Discount code is not valid.",
    };
  }

  if (isCartDiscountCodeApplied(afterCart, normalizedCode)) {
    return { success: true };
  }

  const entry = getCartDiscountCodeEntry(afterCart, normalizedCode);
  if (entry && entry.applicable === false) {
    return {
      success: false,
      message:
        "This discount code can't be used with your current cart. Check product or order requirements.",
    };
  }

  if (discountTotalsIncreased(beforeCart, afterCart)) {
    return { success: true };
  }

  const afterApps = afterCart?.cart_level_discount_applications || [];
  const matchedApp = afterApps.some((app) => {
    const title = String(app?.title || app?.description || "").toLowerCase();
    return title.includes(normalizedCode);
  });
  if (matchedApp) {
    return { success: true };
  }

  return {
    success: false,
    message: "Discount code is not valid.",
  };
}
